/**
 * What happened to one task: stages, attempts, evidence (S-03, AC-034).
 *
 * This was a screen of its own, reached from a list of tasks, which put the
 * answer to "what happened to this card?" two navigations away from the card.
 * It renders inside the open card now (ADR-118), and the standalone route
 * remains for a link that names a task with no card to open.
 *
 * The one thing this must not do is smooth over the read-back barrier: a stage
 * that has *requested* a transition and a stage the board has *confirmed* moved
 * are different states, and the second is what makes the next stage eligible.
 */

import { useMemo, type ReactNode } from 'react';

import { describeStage } from '../../logic/event-phrases.js';
import { formatTimestamp, shortDigest } from '../../logic/format.js';
import { useConsoleMutation, useRoles, useRuns } from '../queries.js';
import { Chip, QueryPhase } from '../components/state.js';
import { Button } from '@primer/react';

function useStageRetry(taskBindingId: string) {
  return useConsoleMutation<string, unknown>((data, context, stageRunId) => data.retryStage(stageRunId, context), [
    ['runs', taskBindingId],
    ['board'],
  ]);
}

function isUnreadableWorkspaceBlock(note: string): boolean {
  const normalized = note.toLowerCase();
  return normalized.includes('workspace could not be read') || normalized.includes('could not read its workspace');
}

function isExitWithoutOutcome(note: string): boolean {
  return note.toLowerCase().includes('agent_exit_without_outcome');
}

function hasPreservedProgress(note: string): boolean {
  return note.toLowerCase().includes('last durable progress report:');
}

function extractPreservedProgress(note: string): string | null {
  const marker = 'Last durable progress report:';
  const offset = note.indexOf(marker);
  return offset < 0 ? null : note.slice(offset + marker.length).trim();
}

export function BlockedStageResume({ taskBindingId, currentStageRunId }: { taskBindingId: string; currentStageRunId: string | null }): ReactNode {
  const runs = useRuns(taskBindingId);
  const retry = useStageRetry(taskBindingId);
  const blockedStage = currentStageRunId === null ? null : (runs.data ?? []).flatMap((run) => run.stages).find(
    (stage) => stage.stageRunId === currentStageRunId && stage.state === 'BLOCKED_HUMAN' && isUnreadableWorkspaceBlock(stage.note),
  ) ?? null;
  if (blockedStage === null) return null;
  const missingOutcome = isExitWithoutOutcome(blockedStage.note);
  const preservedProgress = hasPreservedProgress(blockedStage.note);
  const progressEvidence = preservedProgress ? extractPreservedProgress(blockedStage.note) : null;
  return (
    <section className="task-attention" aria-label="Resume blocked automation">
      <div>
        <span className="task-overview-kicker">Infrastructure recovery</span>
        <strong>{missingOutcome ? 'The agent exited without choosing a stage outcome.' : 'The agent finished, but its workspace could not be read.'}</strong>
        <small>{missingOutcome
          ? preservedProgress
            ? `Its last durable progress report is preserved below. Resume the same ${blockedStage.stageId} stage so it can verify that evidence and call finish; progress alone is never treated as success.`
            : `No durable progress report was recorded. Resume the same ${blockedStage.stageId} stage so it can inspect its current state and call finish; a clean process exit is never treated as success.`
          : `Resume the same ${blockedStage.stageId} stage with a higher fence. The agent must inspect the preserved workspace before making another mutation.`}</small>
        {progressEvidence === null ? null : <pre className="task-attention-evidence">{progressEvidence}</pre>}
      </div>
      <div className="task-attention-actions">
        <Button variant="primary" disabled={retry.isPending} onClick={() => retry.mutate(blockedStage.stageRunId)}>
          {retry.isPending ? 'Queuing retry…' : `Resume ${blockedStage.stageId}`}
        </Button>
      </div>
    </section>
  );
}

export function RunTimeline({ taskBindingId }: { taskBindingId: string }): ReactNode {
  const runs = useRuns(taskBindingId);
  const roles = useRoles();

  const roleNames = useMemo(
    () => new Map((roles.data ?? []).map((role) => [role.roleInstanceId, role.displayName] as const)),
    [roles.data],
  );

  const cancel = useConsoleMutation<string, unknown>((data, context, runId) => data.cancelRun(runId, context), [
    ['runs', taskBindingId],
  ]);
  const retry = useStageRetry(taskBindingId);

  return (
    <>
      <QueryPhase
        isPending={runs.isPending}
        error={runs.error}
        isEmpty={runs.data?.length === 0}
        source="runs"
        emptyTitle="No agent has worked on this yet"
        emptyDetail="Stages and attempts appear here once a run starts."
        onRetry={() => void runs.refetch()}
        rows={3}
      />

      {(runs.data ?? []).map((run) => (
        <article className="run" key={run.runId}>
          <header className="run-head">
            <h4>{run.workflow}</h4>
            <span className="run-meta">
              team version {run.teamVersion} · started {formatTimestamp(run.startedAt)} · {shortDigest(run.runId)}
            </span>
            <Button onClick={() => cancel.mutate(run.runId)}>
              Cancel run
            </Button>
          </header>

          <ul className="timeline">
            {run.stages.map((stage) => (
              <li key={stage.stageRunId}>
                <div className="timeline-head">
                  <span className="timeline-stage">{stage.stageId}</span>
                  <Chip tone={describeStage(stage.state).tone} text={describeStage(stage.state).text} />
                  <span className="timeline-role">
                    {stage.roleInstanceId === null
                      ? 'unassigned'
                      : roleNames.get(stage.roleInstanceId) ?? stage.roleInstanceId}
                  </span>
                  {/* The enum stays: it is what an incident report will quote. */}
                  <code className="timeline-state">{stage.state}</code>
                  {stage.state === 'FAILED' || stage.state === 'BLOCKED_HUMAN' ? (
                    <Button disabled={retry.isPending} onClick={() => retry.mutate(stage.stageRunId)}>
                      {retry.isPending ? 'Queuing retry…' : stage.state === 'BLOCKED_HUMAN' ? 'Resolve and retry stage' : 'Retry failed stage'}
                    </Button>
                  ) : null}
                </div>

                {stage.requestedTransition !== null && stage.transitionCommittedAt === null ? (
                  <p className="timeline-barrier">
                    Requested {stage.requestedTransition}; the next stage stays ineligible until the board confirms it
                    (AC-034).
                  </p>
                ) : null}

                {stage.attempts.length === 0 ? null : (
                  <ul className="attempt-list">
                    {stage.attempts.map((attempt) => (
                      <li className="attempt" key={attempt.attemptId}>
                        <span className="attempt-fence">fence {attempt.fence}</span>
                        <span>{attempt.model}</span>
                        <span className="attempt-outcome">{attempt.outcome}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </article>
      ))}
    </>
  );
}
